import React from 'react';
import {useHistory} from 'react-router-dom';

import {type RouteConfigPath} from './route-config-path';
import {type PathGenerationArgs} from './path-generation-args';
import {generatePath} from './generate-path';

/**
 * @returns A typesafe function that can push a new path to the history stack.
 *
 * The function takes a variable number of arguments that are used to generate a path.
 * The arguments are the same as the ones used in `generateHash`.
 *
 * @example
 * ```tsx
 * const transitionToPath = useTransitionToPath();
 *
 * transitionToPath('/account/childProfile/:childId/activity', {childId});
 * ```
 */
export const useTransitionToPath = () => {
    const {push} = useHistory();

    const transitionToPath = React.useCallback(
        <T extends RouteConfigPath>(...args: PathGenerationArgs<T>) => {
            const hash = generatePath(args);
            push(hash);
        },
        [push],
    );

    return transitionToPath;
};
